import { QueryFunction, QueryKey, useQuery as useReactQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { QueryResult, QueryOptions } from './QueryTypes';

const getQueryKey = (key: QueryKey | string) => (typeof key === 'string' ? [key] : key);

const useQuery = <T = unknown, TError = RequestExceptionWithResponse>(
    key: QueryKey | string,
    query: QueryFunction<T>,
    { onSuccess, ...options }: QueryOptions<T, TError> = {},
): QueryResult<T, TError> => {
    const queryClient = useQueryClient({ context: options.context });
    const queryKey = getQueryKey(key);

    const queryState = useReactQuery(queryKey, query, options);

    const queryIsLoading = useMemo(
        () => queryState.isLoading && queryState.fetchStatus !== 'idle',
        [queryState.isLoading, queryState.fetchStatus],
    );

    const successCallback = useRef<((data: T) => void) | undefined>(onSuccess);

    useEffect(() => {
        if (queryState.isSuccess && !queryIsLoading && successCallback.current) {
            successCallback.current(queryState.data);
        }
    }, [queryIsLoading, queryState.isSuccess, queryState.data]);

    return {
        ...queryState,
        isLoading: queryIsLoading,
        invalidateQuery: () => queryClient.invalidateQueries(queryKey),
    };
};

export default useQuery;
